import { TemplateItem } from '@anm/api/modules/templates';
import { VideoFormatType } from '@anm/api/modules/video';
import PreviewTitleCard from '@anm/components/PreviewTitleCard';
import { AspectRatioLabel, ASPECT_MAP } from '@anm/data/aspects';
import formatTime from '@anm/helpers/time/formatTime';
import usePageParams from '@anm/hooks/usePageParams';
import isEmpty from 'lodash/fp/isEmpty';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { filterSelectors } from '../../../store/modules/filter';
import { templateSelectors, CategoryTemplates } from '../../../store/modules/template';
import { selectFormatsByFormat, selectVideoFormats } from '../../../store/modules/video';
import { getTemplateLink } from '../TemplateListWithScroll';

import { DurationBadge, SlideTitle } from './Wrapper';
import { getTemplateMedia } from './helpers';
import { SliderHandlersProps } from './types';

const ITEM_WIDTH = 280;
const DEFAULT_FORMAT = '1:1';

const useCalcPreviewSizes = (templates: CategoryTemplates | undefined) => {
  const [format, setFormat] = useState<VideoFormatType>(DEFAULT_FORMAT);
  const videoFormats = useSelector(selectVideoFormats);

  useEffect(() => {
    const aspects = templates?.params?.aspects;
    const isDefaultFormat = format === DEFAULT_FORMAT;

    if (!aspects && !isDefaultFormat) {
      return setFormat(DEFAULT_FORMAT);
    }

    const strFormats = aspects?.split(',') || [];
    const currentVideoFormat = videoFormats.find(f => f.name === strFormats[0])?.format;

    currentVideoFormat && setFormat(currentVideoFormat);
  }, [templates?.params, videoFormats]);

  const [formatWidthRatio, formatHeightRatio] = format.split(':');

  const previewSizes = {
    width: ITEM_WIDTH,
    height: (ITEM_WIDTH / +formatWidthRatio) * +formatHeightRatio
  };

  return [format, previewSizes] as const;
};

export const useSliderHandlers = ({ search, isUpdateAspects = true, name: pageName }: SliderHandlersProps) => {
  const templates = useSelector(state => templateSelectors.selectTemplatesByCategory(state, pageName));
  const [format, previewSizes] = useCalcPreviewSizes(templates);
  const templateFilters = useSelector(filterSelectors.selectTemplatesFilters);
  const savedFilters = useSelector(filterSelectors.selectSavedFilters);
  const pageParams = usePageParams();

  const filters = isEmpty(templateFilters) ? savedFilters : templateFilters;

  const currentFormats = useSelector(state => selectFormatsByFormat(state, format));

  const seeAllLinkParams = {
    ...(pageName && { pageName }),
    ...(pageParams.embed && { embed: pageParams.embed }),
    ...(pageParams.isSemrush && { isSemrush: pageParams.isSemrush })
  };

  const canHideSlider =
    !isUpdateAspects &&
    filters?.aspects &&
    !isEmpty(filters) &&
    search?.aspects !== (ASPECT_MAP[filters?.aspects as AspectRatioLabel] || filters?.aspects);

  const renderTemplate = useCallback(
    (template: TemplateItem) => {
      const mediaProps = getTemplateMedia({ template, currentFormats });
      const { slug, templateId, displayName, meta } = template;

      const isImage = meta.kind === 'image';

      const link = getTemplateLink({
        filters,
        pageParams,
        templateName: slug
      });

      return (
        <PreviewTitleCard
          isLink
          weight="bold"
          key={templateId}
          alt={displayName}
          size={previewSizes}
          // isFirstRenderPreview
          link={link}
          preview={mediaProps.preview}
          videoUrl={isImage ? undefined : mediaProps.videoUrl}
          renderDurationBadge={isImage ? undefined : () => <DurationBadge>{formatTime(meta.duration)}</DurationBadge>}
        >
          <SlideTitle>{displayName}</SlideTitle>
        </PreviewTitleCard>
      );
    },
    [format, pageParams, currentFormats]
  );

  return [templates, canHideSlider, seeAllLinkParams, renderTemplate] as const;
};
